import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import type { Socket } from "socket.io-client";
import {
  CarouselDots,
  ClippedPillButton,
  JazzLogoImg,
  LobbyBackground,
  LobbyContent,
  LobbyFooterBar,
  LogoImg,
  LogoRow,
  RegulatoryCode,
  RulesCard,
  StepDots,
  WelcomeHeading,
} from "../components/blueShell";
import {
  CardBody,
  FixedQuestionCard,
  OptionBadge,
  PrimaryPillButton as NextButton,
  QuestionCardWrapper,
  QuestionHeaderRow,
  QuestionStepDots,
  QuestionTitleBlock,
  SlideImage,
  SvgSlideDeck,
  TimerBadge,
  TimerLabel,
  TimerSeconds,
} from "../components/quizChrome";
import { Button, PageContainer, Spinner } from "../components/shared";
import { questions } from "../game/questions";
import { GameState, Phase } from "../game/types";
import { useCountdown } from "../game/useCountdown";
import { useGameSocket } from "../game/useGameSocket";
import { colors } from "../theme/colors";

// SVG filename for each explanation slide, per question index (index 0 = Q1, ... index 14 = Q15).
// Filled in as assets are provided; questions without an entry fall back to the
// "Explanation slider N" placeholder and skip straight to the score screen.
const EXPLANATION_SLIDE_IMAGES: Record<number, string[]> = {
  0: ["Q1.svg", "Q1.1.svg"],
  1: ["Q2.svg"],
  2: ["Q3.1.svg", "Q3.2.svg"],
  3: ["Q4.svg"],
  4: ["Q5.1.svg"],
  5: ["Q6.svg"],
  6: ["Q7.svg", "Q7.1.svg", "Q7.2.svg"],
  7: ["Q8.svg"],
  8: ["Q9.svg"],
  9: ["Q10.svg"],
  10: ["Q11.svg", "Q11.1.svg"],
  11: ["Q12.svg"],
  12: ["Q13.svg", "Q13.1.svg"],
  13: ["Q14.svg"],
};

const LEGAL_CONTENT_SLIDES = [
  "ML.svg",
  ...Array.from({ length: 16 }, (_, i) => `DONNÉES DE SÉCURITÉ ${i + 1}.svg`),
  "INFORMATIONS PRODUIT.svg",
  "conditions-prescriptions.svg",
];

const EXPLANATION_SLIDE_COUNTS = questions.map((_, i) => EXPLANATION_SLIDE_IMAGES[i]?.length ?? 0);

const ModeratorSocketContext = createContext<Socket | null>(null);

const LobbyTopBar = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const TopBarButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;

function useResetSession(): () => void {
  const socket = useContext(ModeratorSocketContext);
  return () => {
    if (window.confirm("Réinitialiser la session pour l'animateur et les équipes ?")) {
      socket?.emit("moderator:resetGame");
    }
  };
}

const ScanBanner = styled.div`
  display: inline-block;
  margin-top: 1.25rem;
  padding: 0.875rem 5rem;
  border-radius: 999px;
  color: #1a237e;
  background: linear-gradient(to right, #a995f1, #ebe8ef, #e0c5e5, #94eef8, #80caff);
  max-width: 90%;
`;

const LobbyTeamsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1.25rem;
  }
`;

const TeamCard = styled.div`
  background: #fff;
  border-radius: 3rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 10rem;
`;

const QrImage = styled.img`
  width: 6.25rem;
  height: 6.25rem;
  border-radius: 0.5rem;
`;

const TeamPill = styled.div<{ $connected: boolean }>`
  border-radius: 999px;
  padding: 0.375rem 1rem;
  color: #fff;
  font-weight: bold;
  font-size: 0.75rem;
  background-color: ${(props) => (props.$connected ? "#4caf50" : "#9e9e9e")};
`;

const WaitingColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 13.75rem;
`;

const WaitingText = styled.p`
  margin-top: 0.75rem;
  font-weight: bold;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const NoteText = styled.p`
  font-size: 0.75rem;
  margin-top: 0.75rem;
  text-align: justify;
  color: ${colors.navy};
`;

function renderNote(note: string) {
  const separatorIndex = note.indexOf(":");
  if (separatorIndex === -1) {
    return note;
  }
  return (
    <>
      <strong>{note.slice(0, separatorIndex + 1)}</strong>
      {note.slice(separatorIndex + 1)}
    </>
  );
}

const Actions = styled.div`
  margin-top: 1.25rem;
`;

const SlideBox = styled.div`
  border: 2px solid #000;
  padding: 2.5rem;
  min-height: 12.5rem;
`;

const QuestionBackground = styled.div<{ $blue?: boolean }>`
  min-height: 100vh;
  box-sizing: border-box;
  background-image: url("/images/${(props) => (props.$blue ? "bg_blue.png" : "bg_green.png")}");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const QuestionContent = styled.div`
  flex: 1;
  padding: 1.5rem 2.5rem 2.5rem;

  @media (max-width: 600px) {
    padding: 1.5rem 1rem 2rem;
  }
`;

const QuestionTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const OptionRow = styled.div<{ $correct: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  color: #fff;
  background-color: ${(props) => (props.$correct ? "#4caf50" : colors.navy)};
`;

const NextButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const QuestionFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
`;

const QuestionFooterLogoImg = styled.img`
  height: 1.5rem;
`;

const ScoreTeamsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 3rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const ScoreTeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 13rem;
`;

const ScoreCard = styled.div`
  background: #fff;
  border-radius: 3.5rem;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
`;

const TeamNamePill = styled.div`
  background: ${colors.navy};
  color: #fff;
  font-weight: bold;
  font-size: 0.8125rem;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 0.5rem 2.25rem;
`;

const ScoreCircleWrapper = styled.div`
  position: relative;
  width: 5rem;
  height: 5rem;

  span {
    position: absolute;
    border-radius: 50%;
    background: ${colors.green};
  }

  span:nth-child(1) {
    top: 0;
    left: 0.25rem;
    width: 0.375rem;
    height: 0.375rem;
  }

  span:nth-child(2) {
    top: 0.5rem;
    right: 0;
    width: 0.25rem;
    height: 0.25rem;
  }

  span:nth-child(3) {
    bottom: 0.25rem;
    left: -0.25rem;
    width: 0.25rem;
    height: 0.25rem;
  }

  span:nth-child(4) {
    bottom: 0;
    right: 0.5rem;
    width: 0.375rem;
    height: 0.375rem;
  }
`;

const ScoreCircle = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background-color: ${colors.green};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
`;

const ScoreResultLabel = styled.p`
  color: ${colors.navy};
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;
  margin: 0;
`;

const ScoreBanner = styled.div<{ $correct: boolean }>`
  padding: 0.625rem 1.25rem;
  border-radius: 999px;
  color: #fff;
  font-weight: bold;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => (props.$correct ? colors.green : colors.red)};

  strong {
    display: block;
  }
`;

const WinnerBanner = styled.div`
  background: linear-gradient(to right, #a995f1, #ebe8ef, #e0c5e5, #94eef8, #80caff);
  color: ${colors.navy};
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;
  border-radius: 999px;
  padding: 1rem 1.5rem;
  margin: 1.5rem auto 0;
  max-width: 32rem;
`;

const BeginQuizButton = styled(Button)`
  background: ${colors.navy};
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.875rem 1.5rem;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.8125rem;
  margin-top: 1.25rem;
  width: 85%;
`;

const ScoreNextButton = styled(Button)`
  background: #fff;
  color: ${colors.navy};
  border: none;
  border-radius: 999px;
  padding: 0.625rem 2rem;
  font-weight: bold;
  margin-top: 1.5rem;
`;

function LegalContentScreen({ onClose }: { onClose: () => void }) {
  const [currentSlide, setCurrentSlide] = useState(LEGAL_CONTENT_SLIDES[0]);

  return (
    <QuestionBackground $blue={currentSlide === "conditions-prescriptions.svg"}>
      <QuestionContent>
        <QuestionTopBar>
          <ClippedPillButton onClick={onClose}>Quitter les mentions légales</ClippedPillButton>
          {/* Epidyolex logo hidden for now */}
        </QuestionTopBar>
        <SvgSlideDeck slides={LEGAL_CONTENT_SLIDES} onSlideChange={setCurrentSlide} />
      </QuestionContent>
      <QuestionFooter>
        <QuestionFooterLogoImg src="/images/JazzPharma_Logo_BL_1.png" alt="Jazz Pharmaceuticals" />
      </QuestionFooter>
    </QuestionBackground>
  );
}

function GameShell({
  heading,
  showLogo = true,
  children,
}: {
  heading?: string;
  showLogo?: boolean;
  children: ReactNode;
}) {
  const [showLegal, setShowLegal] = useState(false);
  const resetSession = useResetSession();

  if (showLegal) {
    return <LegalContentScreen onClose={() => setShowLegal(false)} />;
  }

  return (
    <LobbyBackground>
      <LobbyContent>
        <LobbyTopBar>
          <TopBarButtonStack>
            <ClippedPillButton onClick={() => setShowLegal(true)}>Accès aux mentions légales</ClippedPillButton>
            <ClippedPillButton onClick={resetSession}>Réinitialiser la session</ClippedPillButton>
          </TopBarButtonStack>
        </LobbyTopBar>
        {heading && <WelcomeHeading>{heading}</WelcomeHeading>}
        {showLogo && (
          <LogoRow>
            <LogoImg src="/images/LOGO_EPI.png" alt="EPI & CO" />
          </LogoRow>
        )}
        {children}
      </LobbyContent>
      <LobbyFooterBar>
        <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
        <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
      </LobbyFooterBar>
    </LobbyBackground>
  );
}

function QuestionShell({ children }: { children: ReactNode }) {
  const [showLegal, setShowLegal] = useState(false);
  const resetSession = useResetSession();

  if (showLegal) {
    return <LegalContentScreen onClose={() => setShowLegal(false)} />;
  }

  return (
    <QuestionBackground>
      <QuestionContent>
        <QuestionTopBar>
          <TopBarButtonStack>
            <ClippedPillButton onClick={() => setShowLegal(true)}>Accès aux mentions légales</ClippedPillButton>
            <ClippedPillButton onClick={resetSession}>Réinitialiser la session</ClippedPillButton>
          </TopBarButtonStack>
          {/* Epidyolex logo hidden for now */}
        </QuestionTopBar>
        {children}
      </QuestionContent>
      <QuestionFooter>
        <QuestionFooterLogoImg src="/images/JazzPharma_Logo_BL_1.png" alt="Jazz Pharmaceuticals" />
      </QuestionFooter>
    </QuestionBackground>
  );
}

function LobbyView({ connected }: { connected: { player1: boolean; player2: boolean } }) {
  const bothConnected = connected.player1 && connected.player2;

  return (
    <GameShell heading="BIENVENUE SUR">
      <ScanBanner>SCANNER LE QR CODE POUR COMMENCER !</ScanBanner>
      <CarouselDots>
        <span />
        <span />
        <span />
      </CarouselDots>
      <LobbyTeamsRow>
        <TeamCard>
          <QrImage src="/images/qr-equipe-1.svg" alt="QR code équipe 1" />
          <TeamPill $connected={connected.player1}>ÉQUIPE 1</TeamPill>
        </TeamCard>
        <WaitingColumn>
          <Spinner />
          <WaitingText>
            {bothConnected
              ? "L'animateur peut appuyer sur une touche pour commencer"
              : "En attente de la connexion des équipes"}
          </WaitingText>
        </WaitingColumn>
        <TeamCard>
          <QrImage src="/images/qr-equipe-2.svg" alt="QR code équipe 2" />
          <TeamPill $connected={connected.player2}>ÉQUIPE 2</TeamPill>
        </TeamCard>
      </LobbyTeamsRow>
    </GameShell>
  );
}

function RulesView({ onBeginQuiz }: { onBeginQuiz: () => void }) {
  return (
    <GameShell>
      <RulesCard>
        <h2>Les règles du quiz</h2>
        <StepDots>
          <span />
          <span />
          <span />
        </StepDots>
        <p><strong>Prêts à relever le défi en équipe ?</strong></p>
        <p>
          L'objectif est simple : <strong>accumuler le plus de bonnes réponses.</strong>
        </p>
        <p>
          <strong>Attention au temps !</strong> Vous devrez vous concerter et voter avant que le
          chronomètre ne s'arrête.
        </p>
        <p><strong>Bon quiz !</strong></p>
        <BeginQuizButton onClick={onBeginQuiz}>
          Appuyer sur une touche pour lancer la 1ère question
        </BeginQuizButton>
      </RulesCard>
    </GameShell>
  );
}

interface QuestionViewProps {
  phase: Phase;
  questionIndex: number;
  timerEndsAt: number | null;
  canAdvance: boolean;
  onNext: () => void;
  onStartExplanationSlides: () => void;
  onExplanationDone: () => void;
}

function QuestionView({
  phase,
  questionIndex,
  timerEndsAt,
  canAdvance,
  onNext,
  onStartExplanationSlides,
  onExplanationDone,
}: QuestionViewProps) {
  const question = questions[questionIndex];
  const remainingSeconds = useCountdown(phase === "QUESTION" ? timerEndsAt : null);
  const [displaySeconds, setDisplaySeconds] = useState(remainingSeconds);
  useEffect(() => {
    if (phase === "QUESTION") {
      setDisplaySeconds(remainingSeconds);
    }
  }, [phase, remainingSeconds]);
  const slideCount = EXPLANATION_SLIDE_COUNTS[questionIndex] ?? 0;

  // 0 = answer reveal, 1..slideCount = explanation slides.
  const [explanationStep, setExplanationStep] = useState(0);

  useEffect(() => {
    setExplanationStep(0);
  }, [questionIndex]);

  if (phase === "EXPLANATION" && explanationStep > 0) {
    const isLastSlide = explanationStep === slideCount;
    const slideImage = EXPLANATION_SLIDE_IMAGES[questionIndex]?.[explanationStep - 1];
    return (
      <QuestionShell>
        <QuestionCardWrapper>
          <FixedQuestionCard>
            <CardBody>
              {slideImage ? (
                <SlideImage src={`/images/${slideImage}`} alt={`Explication ${explanationStep}`} />
              ) : (
                <SlideBox>Explanation slider {explanationStep}</SlideBox>
              )}
            </CardBody>
            <NextButtonRow>
              <NextButton onClick={() => (isLastSlide ? onExplanationDone() : setExplanationStep((s) => s + 1))}>
                Suivant
              </NextButton>
            </NextButtonRow>
          </FixedQuestionCard>
        </QuestionCardWrapper>
      </QuestionShell>
    );
  }

  const revealed = phase === "EXPLANATION";

  return (
    <QuestionShell>
      <QuestionCardWrapper>
        <FixedQuestionCard>
          <CardBody>
            <QuestionHeaderRow>
              <QuestionTitleBlock>
                <h2>{question.title}</h2>
                <QuestionStepDots>
                  <span />
                  <span />
                  <span />
                </QuestionStepDots>
              </QuestionTitleBlock>
              {(phase === "QUESTION" || phase === "LOCKED") && (
                <TimerBadge>
                  <TimerSeconds>{displaySeconds}</TimerSeconds>
                  <TimerLabel>sec</TimerLabel>
                </TimerBadge>
              )}
            </QuestionHeaderRow>
            <OptionsList>
              {question.options.map((option) => {
                const isCorrect = question.correctAnswers.includes(option.key);
                return (
                  <OptionRow key={option.key} $correct={revealed && isCorrect}>
                    <OptionBadge>{option.key}</OptionBadge>
                    <span>{option.text}</span>
                  </OptionRow>
                );
              })}
            </OptionsList>
            {question.note && <NoteText>{renderNote(question.note)}</NoteText>}
          </CardBody>
          <NextButtonRow>
            <NextButton
              onClick={() => {
                if (revealed) {
                  if (slideCount > 0) {
                    onStartExplanationSlides();
                    setExplanationStep(1);
                  } else {
                    onExplanationDone();
                  }
                } else {
                  onNext();
                }
              }}
              disabled={!canAdvance}
            >
              Suivant
            </NextButton>
          </NextButtonRow>
        </FixedQuestionCard>
      </QuestionCardWrapper>
    </QuestionShell>
  );
}

function ScoreView({
  answers,
  scores,
  onContinue,
}: {
  answers: GameState["answers"];
  scores: GameState["scores"];
  onContinue: () => void;
}) {
  const teams: Array<{ role: "player1" | "player2"; label: string }> = [
    { role: "player1", label: "ÉQUIPE 1" },
    { role: "player2", label: "ÉQUIPE 2" },
  ];

  return (
    <GameShell heading="Classement des équipes" showLogo={false}>
      <StepDots>
        <span />
        <span />
        <span />
      </StepDots>
      <ScoreTeamsRow>
        {teams.map(({ role, label }) => {
          const isCorrect = answers[role]?.isCorrect === true;
          const score = scores[role];
          return (
            <ScoreTeamColumn key={role}>
              <ScoreCard>
                <TeamNamePill>{label}</TeamNamePill>
                <ScoreCircleWrapper>
                  <span />
                  <span />
                  <span />
                  <span />
                  <ScoreCircle>{score}</ScoreCircle>
                </ScoreCircleWrapper>
                <ScoreResultLabel>
                  {score <= 1 ? "Bonne réponse" : "Bonnes réponses"}
                </ScoreResultLabel>
              </ScoreCard>
              <ScoreBanner $correct={isCorrect}>
                {isCorrect ? (
                  <>
                    Bonne réponse
                    <strong>+1 POINT</strong>
                  </>
                ) : (
                  <>
                    Mauvaise réponse
                    <strong>+0 POINT</strong>
                  </>
                )}
              </ScoreBanner>
            </ScoreTeamColumn>
          );
        })}
      </ScoreTeamsRow>
      <Actions>
        <ScoreNextButton onClick={onContinue}>Suivant</ScoreNextButton>
      </Actions>
    </GameShell>
  );
}

function FinalScoreView({
  scores,
  onContinue,
}: {
  scores: GameState["scores"];
  onContinue: () => void;
}) {
  const teams: Array<{ role: "player1" | "player2"; label: string }> = [
    { role: "player1", label: "ÉQUIPE 1" },
    { role: "player2", label: "ÉQUIPE 2" },
  ];

  const winnerMessage =
    scores.player1 === scores.player2
      ? "Égalité entre les deux équipes !"
      : `Félicitations, l'équipe ${scores.player1 > scores.player2 ? "1" : "2"} a cumulé le plus de bonnes réponses !`;

  return (
    <GameShell heading="Classement des équipes" showLogo={false}>
      <StepDots>
        <span />
        <span />
        <span />
      </StepDots>
      <ScoreTeamsRow>
        {teams.map(({ role, label }) => {
          const score = scores[role];
          return (
            <ScoreTeamColumn key={role}>
              <ScoreCard>
                <TeamNamePill>{label}</TeamNamePill>
                <ScoreCircleWrapper>
                  <span />
                  <span />
                  <span />
                  <span />
                  <ScoreCircle>{score}</ScoreCircle>
                </ScoreCircleWrapper>
                <ScoreResultLabel>{score <= 1 ? "Bonne réponse" : "Bonnes réponses"}</ScoreResultLabel>
              </ScoreCard>
            </ScoreTeamColumn>
          );
        })}
      </ScoreTeamsRow>
      <WinnerBanner>{winnerMessage}</WinnerBanner>
      <Actions>
        <ScoreNextButton onClick={onContinue}>Suivant</ScoreNextButton>
      </Actions>
    </GameShell>
  );
}

function EndView({
  scores,
  onRestart,
}: {
  scores: GameState["scores"];
  onRestart: () => void;
}) {
  const [showSummary, setShowSummary] = useState(false);

  if (showSummary) {
    return (
      <QuestionShell>
        <QuestionCardWrapper>
          <FixedQuestionCard>
            <CardBody>
              <SlideImage src="/images/res-info-ess.svg" alt="Résumé des informations essentielles" />
            </CardBody>
            <NextButtonRow>
              <NextButton onClick={onRestart}>Terminer</NextButton>
            </NextButtonRow>
          </FixedQuestionCard>
        </QuestionCardWrapper>
      </QuestionShell>
    );
  }

  return <FinalScoreView scores={scores} onContinue={() => setShowSummary(true)} />;
}

export default function ModeratorPage() {
  const { state, socket } = useGameSocket("moderator");

  if (!state) {
    return <p>Connexion au serveur...</p>;
  }

  const goToNextQuestionOrEnd = () => {
    if (state.questionIndex < questions.length - 1) {
      socket?.emit("moderator:nextQuestion");
    } else {
      socket?.emit("moderator:endGame");
    }
  };

  let content: ReactNode;

  if (state.phase === "LOBBY") {
    content = <LobbyView connected={state.connected} />;
  } else if (state.phase === "RULES") {
    content = <RulesView onBeginQuiz={() => socket?.emit("moderator:beginQuiz")} />;
  } else if (state.phase === "QUESTION" || state.phase === "LOCKED" || state.phase === "EXPLANATION") {
    content = (
      <QuestionView
        phase={state.phase}
        questionIndex={state.questionIndex}
        timerEndsAt={state.timerEndsAt}
        canAdvance={state.canAdvance}
        onNext={() => socket?.emit("moderator:next")}
        onStartExplanationSlides={() => socket?.emit("moderator:startExplanationSlides")}
        onExplanationDone={() => socket?.emit("moderator:next")}
      />
    );
  } else if (state.phase === "SCORE") {
    content = (
      <ScoreView answers={state.answers} scores={state.scores} onContinue={goToNextQuestionOrEnd} />
    );
  } else if (state.phase === "END") {
    content = <EndView scores={state.scores} onRestart={() => socket?.emit("moderator:resetGame")} />;
  } else {
    content = (
      <PageContainer>
        <h1>Bienvenue sur EPI & CO</h1>
      </PageContainer>
    );
  }

  return <ModeratorSocketContext.Provider value={socket}>{content}</ModeratorSocketContext.Provider>;
}
